import { Col, Row } from "antd";
import { FC, useState } from "react";
import {
  Wrapper,
  HeaderText,
  Container,
  FailedReasonWrapper,
  RightSideWrapper,
  ApplicantInsightWrapper,
  LostApplicantWrapper,
  FunnelWrapper,
  StyledRangePicker,
  JobOfferWrapper,
  OtherReasonsModalBodyWrapper,
  OtherReasonsModalBodyItem,
  OtherReasonsModalFooterWrapper,
} from "./elements";
import RecruitmentFunnel from "./../../organisms/RecruitmentFunnel/RecruitmentFunnel";
import { JobOfferInsights } from "../../organisms/JobOfferInsights";
import { ApplicantInsights } from "../../organisms/ApplicantInsights";
import { FailedReasonInsights } from "../../organisms/FailedReasonInsights";
import { BarGraphInsight } from "../../organisms/BarGraphInsight";
import { Modal } from "../../organisms/Modal";
import moment from "moment";
import { Button } from "../../atoms/Button";

type Props = {
  funnelData: {
    applicants: {
      passed: number;
      failed: number;
    };
    shortlisted: {
      passed: number;
      failed: number;
    };
    technical: {
      passed: number;
      failed: number;
    };
    initialInterview: {
      passed: number;
      failed: number;
    };
    psychological: {
      passed: number;
      failed: number;
    };
    finalInterview: {
      passed: number;
      failed: number;
    };
  };
  jobOfferData: { accepted: number; declined: number; waiting: number };
  applicantInsightsData: { hired: number; failed: number };
  failedReasonsData: Array<{ type: string; value: number; percentage: number }>;
  otherFailedReasonsData: string[];
  lostApplicantData: Array<{
    name: string;
    value: number;
    barColor: string;
    percentage: number;
  }>;
  applicationSources: Array<{
    name: string;
    value: number;
    barColor: string;
    percentage: number;
  }>;
  defaultDateRange?: any;
  onChange?: (e: any) => void;
  onClickMenu?: (e: any) => void;
  isLoading?: boolean;
};

const Insights: FC<Props> = ({
  funnelData,
  jobOfferData,
  applicantInsightsData,
  failedReasonsData,
  otherFailedReasonsData,
  lostApplicantData,
  applicationSources,
  defaultDateRange,
  onChange,
  onClickMenu,
  isLoading,
}) => {
  const [dateFilter, setDateFilter] = useState<Array<any>>([
    moment().startOf("month"),
    moment().add(1, "d").startOf("day"),
  ]);

  const handleOnChangeDate = (e: any) => {
    if (onChange) {
      onChange(e);
    }
   
    if (e) {
      setDateFilter([...e]);
    }
  };

  const [showOtherReasonsModal, setShowOtherReasonsModal] = useState<boolean>(false)

  const toggleOtherReasonsModal = () => {
    setShowOtherReasonsModal((prevState) => !prevState)
  }

  const otherFailedReasons = otherFailedReasonsData.filter(Boolean);

  const otherReasonsModalBody = (
    <>
      <OtherReasonsModalBodyWrapper>
        {otherFailedReasonsData.map((item: any) => (
          <OtherReasonsModalBodyItem key={item}>
            {item}
          </OtherReasonsModalBodyItem>
        ))}
      </OtherReasonsModalBodyWrapper>
      <OtherReasonsModalFooterWrapper>
        <Button type="primary" size="small" onClick={toggleOtherReasonsModal}>
          Close
        </Button>
      </OtherReasonsModalFooterWrapper>
    </>
  );

  return (
    <>
      <Wrapper>
        <HeaderText>Insights</HeaderText>
        <Row gutter={[16, 0]}>
          <Col md={8} lg={10} xl={8} xxl={8}>
            <StyledRangePicker
              className="insightRangPicker"
              format="MM/DD/YYYY"
              separator={<span>-</span>}
              defaultValue={
                defaultDateRange || [moment().startOf("month"), moment()]
              }
              onChange={handleOnChangeDate}
              disabledDate={(current) => {
                if (
                  new Date(moment().format("MM/DD/YYYY").valueOf()) <
                  new Date(current.format("MM/DD/YYYY").valueOf())
                ) {
                  return true;
                }
                if (
                  new Date(
                    moment().subtract(1, "years").format("MM/DD/YYYY").valueOf()
                  ) > new Date(current.format("MM/DD/YYYY").valueOf())
                ) {
                  return true;
                }
                return false;
              }}
            />
            <Container>
              <FunnelWrapper>
                <RecruitmentFunnel
                  isLoading={isLoading}
                  data={funnelData}
                  dateRange={dateFilter}
                  onClick={() => {}}
                />
              </FunnelWrapper>
              <JobOfferWrapper>
                <JobOfferInsights dateRange={dateFilter} item={jobOfferData} />
              </JobOfferWrapper>
            </Container>
          </Col>
          <Col md={16} lg={14} xl={16} xxl={16}>
            <Container>
              <RightSideWrapper>
                <ApplicantInsightWrapper>
                  <ApplicantInsights
                    dateRange={dateFilter}
                    onClickMenu={onClickMenu}
                    type="hired"
                    count={applicantInsightsData.hired}
                  />
                  <ApplicantInsights
                    dateRange={dateFilter}
                    onClickMenu={onClickMenu}
                    type="failed"
                    count={applicantInsightsData.failed}
                  />
                </ApplicantInsightWrapper>
              </RightSideWrapper>
              <FailedReasonWrapper>
                <FailedReasonInsights
                  dateRange={dateFilter}
                  items={failedReasonsData}
                  onClickShowOthers={toggleOtherReasonsModal}
                  failedReasons={otherFailedReasons}
                />
              </FailedReasonWrapper>
              <LostApplicantWrapper isLoss={true}>
                <BarGraphInsight
                  isLoss={true}
                  dateRange={dateFilter}
                  data={lostApplicantData}
                  isLoading={isLoading}
                />
              </LostApplicantWrapper>
              <LostApplicantWrapper isLoss={false}>
                <BarGraphInsight
                  isLoss={false}
                  dateRange={dateFilter}
                  data={applicationSources}
                  isLoading={isLoading}
                />
              </LostApplicantWrapper>
            </Container>
          </Col>
        </Row>
      </Wrapper>
      <Modal
        visible={showOtherReasonsModal}
        title="Other Reasons"
        body={otherReasonsModalBody}
        onModalClose={toggleOtherReasonsModal}
        closable={false}
      />
    </>
  );
};

export default Insights;
